// TODO: Migration ot translatedCopy.js of raw strings
// TODO: Using generative voice service to enable generative at mount

import React, { useState, useEffect, useRef, useMemo } from 'react';
import AmiraIconButton, { ICON_BUTTON_TYPES } from '../Global/AmiraIconButton2/AmiraIconButton2';
import InputCard from '../InputCard/InputCard';
import ArrowIcon from '../Global/Icons/ArrowIcon';
import cx from 'classnames';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import { PhraseTriggers } from '../../constants/constants';
import { getCopyByGradeband } from '../../services/ThemingService';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { debounceWithImmediateTrigger } from '../../services/util';

const IDLE_ENCOURAGEMENT_TIMEOUT = 20000;
const IDLE_SUBMIT_TIMEOUT = 60000;

// Returns the number of grid columns for the tile layout.
// ≤8 tiles: single row. 9+: two rows max (ceil(N/2) cols).
function tileColumns(count) {
  if (count <= 8) return count;
  return Math.ceil(count / 2);
}

const Spelling = ({
  speech,
  word,
  letterDisplay,
  isInstruction,
  isRepeating,
  handleRepeatInstructions,
  repeatInstructions,
  setTextResponse,
  setHideParentRepeatButton,
  currentIntroductionTrigger,
  useTyping
}) => {

  // State
  const [typedValue, setTypedValue] = useState("");   // typing mode only
  const [inputQueue, setInputQueue] = useState([]);
  const [availableLetters, setAvailableLetters] = useState([]);
  const [inFTUE, setInFTUE] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hideLetters, setHideLetters] = useState(false);
  const [hideSubmit, setHideSubmit] = useState(false);
  const [hideInputControls, setHideInputControls] = useState(false);
  const [hasPlayedIdleEncouragement, setHasPlayedIdleEncouragement] = useState(false);

  // Tile mode derives inputValue from the queue; typing mode owns it via typedValue.
  const inputValue = useTyping ? typedValue : inputQueue.join('');

  // True once the student has interacted with the current word. Prevents
  // setTextResponse from firing on mount and on word-change resets.
  const hasTyped = useRef(false);

  const lastLetterClickTime = useRef({});
  const prevClickedValue = useRef("");

  const idleEncouragementText = useTyping
    ? getCopyByGradeband(COPY_STRINGS, 'spelling_idle_typing_encouragement') + repeatInstructions
    : getCopyByGradeband(COPY_STRINGS, 'spelling_idle_letter_click_encouragement') + repeatInstructions;
    
  const { resetIdleTimer, clearIdleTimer, idleSpeechPlaying, idleTimeoutExpired } = useIdleTimer(
    speech, 
    IDLE_ENCOURAGEMENT_TIMEOUT, 
    IDLE_SUBMIT_TIMEOUT
  );

  // Memos
  const tileRows = useMemo(() => {
    const cols = tileColumns(availableLetters.length);
    const rows = [];
    for (let i = 0; i < availableLetters.length; i += cols) {
      rows.push(availableLetters.slice(i, i + cols));
    }
    return { rows, cols };
  }, [availableLetters]);

  const disableAll = useMemo(() => {
    return (isInstruction || isRepeating || loading || idleSpeechPlaying);
  }, [isInstruction, isRepeating, loading, idleSpeechPlaying]);

  const disableSubmit = useMemo(() => {
    return (disableAll || inputValue.length < 1) && currentIntroductionTrigger !== PhraseTriggers.Submit;
  }, [disableAll, inFTUE, inputValue, currentIntroductionTrigger]);

  // Refs
  const inputElement = useRef(null);

  // Handlers
  const handleLetterClick = (letter) => {
    if (inFTUE || disableAll || useTyping) return;

    const now = Date.now();
    const lastClick = lastLetterClickTime.current[letter] || 0;

    // Skip if same letter is clicked within 100ms
    if (letter === prevClickedValue.current && now - lastClick < 100) {
      return;
    }
    lastLetterClickTime.current[letter] = now;
    prevClickedValue.current = letter;

    hasTyped.current = true;
    setInputQueue(prev => [...prev, letter]);
    if (!hasPlayedIdleEncouragement) {
      resetIdleTimer(idleEncouragementText);
    }
  };

  const handleTyping = (event) => {
    if (inFTUE || disableAll || !useTyping) return;
    hasTyped.current = true;
    setTypedValue(event.target.value);
    if (!hasPlayedIdleEncouragement) {
      resetIdleTimer(idleEncouragementText);
    }
  };

  const handleBackspace = () => {
    if (inFTUE || disableAll) return;

    setInputQueue(prev => (prev.length > 0 ? prev.slice(0, -1) : prev));

    // Typing mode: queue is always empty, so fall through to character-level backspace
    if (useTyping && typedValue.length > 0) {
      setTypedValue(prev => prev.slice(0, -1));
    }

    if (!hasPlayedIdleEncouragement) {
      resetIdleTimer(idleEncouragementText);
    }

    if (inputElement && inputElement.current) {
      inputElement.current.focus();
    }
  };

  const handleSubmit = async (forceSubmit) => {
    if ((inFTUE || disableSubmit) && !forceSubmit) return;
    if ((inputValue && inputValue !== "") || forceSubmit) {
      setHasPlayedIdleEncouragement(false); // Reset idle encouragement flag for new item
      clearIdleTimer();
      speech.abortSpeech();
      setLoading(true);

      if (forceSubmit) {
        // If use was idle, we want to speech to notify them we are moving on.
        await speech.speakWithPromise({
          textToSpeak: getCopyByGradeband(COPY_STRINGS, 'lets_move_on')
        });
      }

      // true signals this is the final version of the response
      setTextResponse((inputValue || "").toLowerCase(), true);
    }
  };

  const handleTrigger = (trigger) => {
    switch (trigger) {
      case PhraseTriggers.Start:
        setInFTUE(true);
        setHideInputControls(false);
        setTimeout(() => {
          setHideLetters(false);
          setHideSubmit(false);
        }, 2000);
        break;
      case PhraseTriggers.Repeat:
      case PhraseTriggers.Letters:
      case PhraseTriggers.Backspace:
      case PhraseTriggers.Submit:
        break;
      case PhraseTriggers.Ready:
        setInFTUE(false);
        break;
      default:
        break;
    }
  };

  // Effects
  useEffect(() => {
    // TODO: Handle this only in Phrase, not here
    if (setHideParentRepeatButton) {
      setHideParentRepeatButton(true);
    }

    return () => {
      if (setHideParentRepeatButton) {
        setHideParentRepeatButton(false);
      }
    }
  }, []);

  useEffect(() => {
    if (currentIntroductionTrigger) {
      handleTrigger(currentIntroductionTrigger);
    }
  }, [currentIntroductionTrigger]);

  useEffect(() => {
    if (inputElement && inputElement.current) {
      const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !disableSubmit) {
          handleSubmit();
        }
        if (!useTyping) {
          event.preventDefault(); // Prevent actual typing only if useTyping is false
        }
      };

      inputElement.current.addEventListener("keydown", handleKeyDown);

      return () => {
        if (inputElement && inputElement.current) {
          inputElement.current.removeEventListener("keydown", handleKeyDown);
        }
      };
    }
  }, [disableSubmit, isInstruction, inputElement, useTyping, inputValue]);

  useEffect(() => {
    if (idleTimeoutExpired) {
      const forceSubmit = true;
      handleSubmit(forceSubmit);
    }
  }, [idleTimeoutExpired]);

  // Track when idle encouragement has been played
  useEffect(() => {
    if (idleSpeechPlaying && !hasPlayedIdleEncouragement) {
      setHasPlayedIdleEncouragement(true);
    }
  }, [idleSpeechPlaying, hasPlayedIdleEncouragement]);

  useEffect(() => {
    if (inputElement && inputElement.current && !isInstruction && !isRepeating) {
      setTimeout(() => {
        inputElement.current.focus();
      }, 50);
      setLoading(false);
      if (!hasPlayedIdleEncouragement) {
        resetIdleTimer(idleEncouragementText);
      }
    }
  }, [isInstruction, isRepeating]);

  // When word changes, update the letter display for non-typing users
  useEffect(() => {
    setInFTUE(false);
    setInputQueue([]);
    setTypedValue("");
    hasTyped.current = false;
    if (!useTyping) {
      if (word) {
        // Remove duplicates
        const letters = letterDisplay.includes(',')
          ? letterDisplay.split(',').map(l => l.trim()).filter(Boolean)
          : letterDisplay.split('');
        setAvailableLetters([...new Set(letters)]);
      } else {
        setAvailableLetters([]);
      }
    }
  }, [word, letterDisplay, useTyping]);

  useEffect(() => {
    if (hasTyped.current) {
      setTextResponse(inputValue.toLowerCase());
    }
  }, [inputValue]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--letters-count', availableLetters.length);
  }, [availableLetters]);

  return (
    <div className={`spellingContainer animate ${useTyping ? '' : 'fullscreen'}`}>
      <InputCard
        onRepeat={() => {
          if (!inFTUE && !disableAll) {
            clearIdleTimer();
            handleRepeatInstructions();
          }
        }}
        onBackspace={handleBackspace}
        disableRepeat={disableAll && currentIntroductionTrigger !== PhraseTriggers.Repeat}
        disableBackspace={disableAll && currentIntroductionTrigger !== PhraseTriggers.Backspace}
        hidden={hideInputControls}
        showRepeatPointer={currentIntroductionTrigger === PhraseTriggers.Repeat}
        showBackspacePointer={currentIntroductionTrigger === PhraseTriggers.Backspace}
      >
        <input
          ref={inputElement}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          type="text"
          placeholder=""
          value={inputValue}
          readOnly={!useTyping}
          className={cx(`styledInput pointerTarget`, {
            showPointer: currentIntroductionTrigger === PhraseTriggers.Letters && useTyping,
            sweep: currentIntroductionTrigger === PhraseTriggers.Letters && useTyping,
            disabled: disableAll && !(useTyping && currentIntroductionTrigger === PhraseTriggers.Letters)
          })}
          disabled={disableAll && !(useTyping && currentIntroductionTrigger === PhraseTriggers.Letters)}
          onChange={(event) => {
            handleTyping(event);
          }}
          onContextMenu={(event) => {
            event.preventDefault();
          }}
        />
      </InputCard>
      <div className={cx(`lettersContainer`, {
        disabled: disableAll && currentIntroductionTrigger !== PhraseTriggers.Letters,
        hidden: useTyping || hideLetters
      })}>
        <div
          className={cx('tilesGrid pointerTarget', {
            showPointer: currentIntroductionTrigger === PhraseTriggers.Letters && !useTyping,
            sweep: currentIntroductionTrigger === PhraseTriggers.Letters && !useTyping
          })}
        >
          {tileRows.rows.map((row, rowIndex) => (
            <div key={rowIndex} className="tileRow">
              {row.map((letter, colIndex) => {
                const flatIndex = rowIndex * tileRows.cols + colIndex;
                return (
                  <span
                    key={flatIndex}
                    className="letter"
                    onClick={() => {
                      if (!isInstruction && !isRepeating && !inFTUE) {
                        handleLetterClick(letter);
                      }
                    }}
                    style={{ '--letter-index': flatIndex }}
                  >
                    {letter}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <AmiraIconButton
        type={ICON_BUTTON_TYPES.SUBMIT}
        oblong
        small
        onClick={() => handleSubmit()}
        disabled={disableSubmit}
        invisible={hideSubmit}
        pointerPosition="left"
        showPointer={currentIntroductionTrigger === PhraseTriggers.Submit}
        assessment={true}
      />
    </div>
  );
};

export default Spelling;